import { ArrowLeftIcon , ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Download,
  Image as ImageIcon,
  Loader2,
  PlayCircle,
  RefreshCw,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { FirmCombobox } from '@/components/firms/FirmCombobox';
import { AttorneySelect } from '@/components/matter/AttorneySelect';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useTemplateDeleteMutation,
  useTemplateImagesQuery,
  useTemplateReanalyzeMutation,
  useTemplateRetrieveQuery,
  useTemplateSetDefaultMutation,
  useTemplateUnsetDefaultMutation,
  useTemplateUpdateMutation,
} from '@/services/api/generated/templatesV2Service';

import { BrandedSamplePreview } from './BrandedSamplePreview';
import {
  CATEGORY_OPTIONS,
  SCOPE_OPTIONS,
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_FORMAT_LABELS,
} from './constants';
import { HtmlPreview } from './editor/HtmlPreview';
import { HtmlTemplateEditor } from './editor/HtmlTemplateEditor';
import { SmsPreview } from './editor/SmsPreview';
import { SmsTemplateEditor } from './editor/SmsTemplateEditor';
import { SigningConfigEditor } from './SigningConfigEditor';
import { TemplateFieldsPanel } from './TemplateFieldsPanel';
import { TemplateImagesPanel } from './TemplateImagesPanel';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';
import { TemplateSigningPanel } from './TemplateSigningPanel';
import { TemplateVersionHistory } from './TemplateVersionHistory';
import { downloadTemplate } from './utils/downloadTemplate';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatShortDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

const Badge = ({
  label,
  colorClass,
}: {
  label: string;
  colorClass: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
      colorClass,
    )}
  >
    {label}
  </span>
);

// ---------------------------------------------------------------------------
// Draft types & helpers
// ---------------------------------------------------------------------------

interface TemplateDraft {
  name: string;
  display_name: string;
  category: string;
  scope: string;
  firm_id: string;
  attorney_id: string;
  is_default: boolean;
  is_generic_template: boolean;
  description: string;
  body: string;
}

function buildDraft(template: {
  name: string;
  display_name?: string | null;
  category?: string | null;
  scope: string;
  firm_id?: string | null;
  attorney_id?: string | null;
  is_default?: boolean | null;
  is_generic_template?: boolean | null;
  description?: string | null;
  body?: string | null;
}): TemplateDraft {
  return {
    name: template.name,
    display_name: template.display_name ?? '',
    category: template.category ?? '',
    scope: template.scope,
    firm_id: template.firm_id ?? '',
    attorney_id: template.attorney_id ?? '',
    is_default: !!template.is_default,
    is_generic_template: !!template.is_generic_template,
    description: template.description ?? '',
    body: template.body ?? '',
  };
}

function getDraftChanges(
  draft: TemplateDraft,
  initial: TemplateDraft,
): Partial<TemplateDraft> {
  const changes: Partial<TemplateDraft> = {};
  (Object.keys(initial) as (keyof TemplateDraft)[]).forEach((key) => {
    if (draft[key] !== initial[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (changes as any)[key] = draft[key];
    }
  });
  return changes;
}

// ---------------------------------------------------------------------------
// PageSkeleton
// ---------------------------------------------------------------------------

const PageSkeleton = () => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-4 dark:border-zinc-700">
      <div className="h-5 w-5 rounded bg-zinc-200 dark:bg-zinc-700" />
      <div className="h-6 w-48 rounded bg-zinc-200 dark:bg-zinc-700" />
    </div>
    <div className="flex flex-1 overflow-hidden p-6">
      <div className="w-[35%] animate-pulse space-y-4 pr-4">
        <div className="space-y-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <div className="h-4 w-20 rounded bg-zinc-200 dark:bg-zinc-700" />
              <div className="h-4 w-28 rounded bg-zinc-200 dark:bg-zinc-700" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 animate-pulse rounded-lg bg-zinc-100/50 dark:bg-zinc-900/50" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const selectClass =
  'rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-medium text-zinc-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

const inputClass =
  'w-full rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-medium text-zinc-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateDetailPage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we came from a firm management context
  const searchParams = new URLSearchParams(location.search);
  const firmContext = searchParams.get('firmContext');

  const templatesListPath = firmContext
    ? `/firms/${firmContext}/templates`
    : location.search
      ? `/templates-v2${location.search}`
      : '/templates-v2';
  const buildTemplateDetailPath = (id: string) =>
    location.search
      ? `/templates-v2/${id}${location.search}`
      : `/templates-v2/${id}`;
  const buildTemplateGeneratePath = (id: string) =>
    location.search
      ? `/templates-v2/${id}/generate${location.search}`
      : `/templates-v2/${id}/generate`;

  const [versionPopoverOpen, setVersionPopoverOpen] = useState(false);
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const [signingConfigOpen, setSigningConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showGlobalScopeWarning, setShowGlobalScopeWarning] = useState(false);
  const [showSigningConfigWarning, setShowSigningConfigWarning] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(0);

  // ---- Queries ----
  const { data: template, isLoading } = useTemplateRetrieveQuery(
    { id: templateId! },
    { skip: !templateId, pollingInterval },
  );

  const hasImageSupport =
    template?.format === 'DOCX' || template?.format === 'PDF';

  const { data: images } = useTemplateImagesQuery(
    { id: templateId! },
    { skip: !templateId || !hasImageSupport },
  );

  const imageCount = (images as unknown[] | undefined)?.length ?? 0;
  const [imagesOpen, setImagesOpen] = useState(true);

  // Poll while sample render is in-flight
  useEffect(() => {
    const isRendering =
      template?.sample_render_status === 'QUEUED' ||
      template?.sample_render_status === 'RENDERING';
    setPollingInterval(isRendering ? 2000 : 0);
  }, [template?.sample_render_status]);

  // ---- Mutations ----
  const [updateTemplate] = useTemplateUpdateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] =
    useTemplateDeleteMutation();
  const [setDefault] = useTemplateSetDefaultMutation();
  const [unsetDefault] = useTemplateUnsetDefaultMutation();
  const [reanalyze, { isLoading: isReanalyzing }] =
    useTemplateReanalyzeMutation();

  // ---- Draft state ----
  const initialDraft = useMemo(
    () => (template ? buildDraft(template) : null),
    [template],
  );

  const [draft, setDraft] = useState<TemplateDraft | null>(null);

  // Track which template the draft was built from so we only reset when
  // navigating to a different template — not on every poll tick.
  const draftSourceId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (initialDraft && templateId !== draftSourceId.current) {
      setDraft(initialDraft);
      draftSourceId.current = templateId;
    }
  }, [initialDraft, templateId]);

  const isDirty = useMemo(() => {
    if (!draft || !initialDraft) return false;
    return (Object.keys(initialDraft) as (keyof TemplateDraft)[]).some(
      (key) => draft[key] !== initialDraft[key],
    );
  }, [draft, initialDraft]);

  const updateDraft = useCallback(
    <K extends keyof TemplateDraft>(field: K, value: TemplateDraft[K]) => {
      setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  // ---- Handlers ----
  const handleSave = useCallback(async () => {
    if (!templateId || !draft || !initialDraft) return;
    setSaving(true);
    try {
      const changes = getDraftChanges(draft, initialDraft);

      // Handle is_default via dedicated endpoints
      if ('is_default' in changes) {
        if (draft.is_default) {
          await setDefault({ id: templateId }).unwrap();
        } else {
          await unsetDefault({ id: templateId }).unwrap();
        }
      }

      // Build patch fields (exclude is_default)
      const { is_default: _, ...rest } = changes;
      const patchFields: Record<string, string | boolean | null> = {};
      Object.entries(rest).forEach(([key, value]) => {
        // Booleans (like is_generic_template) should pass through as-is
        if (typeof value === 'boolean') {
          patchFields[key] = value;
        } else if (
          // Convert empty strings to null for nullable fields
          (key === 'description' ||
            key === 'firm_id' ||
            key === 'attorney_id' ||
            key === 'display_name' ||
            key === 'body') &&
          value === ''
        ) {
          patchFields[key] = null;
        } else {
          patchFields[key] = value as string;
        }
      });

      if (Object.keys(patchFields).length > 0) {
        await updateTemplate({
          id: templateId,
          patchedUpdateTemplate: patchFields,
        }).unwrap();
      }

      toast.success('Template updated');
    } catch {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  }, [
    templateId,
    draft,
    initialDraft,
    setDefault,
    unsetDefault,
    updateTemplate,
  ]);

  const handleSaveClick = useCallback(() => {
    // Warn if saving a retainer without a signing configuration
    if (draft?.category === 'RETAINER' && !template?.signing_config) {
      setShowSigningConfigWarning(true);
      return;
    }
    if (draft?.scope === 'GLOBAL') {
      setShowGlobalScopeWarning(true);
    } else {
      handleSave();
    }
  }, [draft?.category, draft?.scope, template?.signing_config, handleSave]);

  const handleDiscard = useCallback(() => {
    if (initialDraft) {
      setDraft(initialDraft);
    }
  }, [initialDraft]);

  const handleDelete = useCallback(async () => {
    if (!templateId) return;
    try {
      await deleteTemplate({ id: templateId }).unwrap();
      toast.success('Template deleted');
      navigate(templatesListPath);
    } catch {
      toast.error('Failed to delete template');
    }
  }, [templateId, deleteTemplate, navigate, templatesListPath]);

  const handleReanalyze = useCallback(async () => {
    if (!templateId) return;
    try {
      const result = await reanalyze({ id: templateId }).unwrap();
      if (result.analysis_errors && result.analysis_errors.length > 0) {
        result.analysis_errors.forEach((err) => {
          toast.error(err.message, {
            description: err.detail ?? undefined,
            duration: 8000,
          });
        });
      } else {
        toast.success('Template re-analysis complete');
      }
    } catch {
      toast.error('Failed to start re-analysis');
    }
  }, [templateId, reanalyze]);

  // ---- Loading state ----
  if (isLoading || !template || !draft) {
    return <PageSkeleton />;
  }

  const formatLabel =
    TEMPLATE_FORMAT_LABELS[template.format] ?? template.format;

  return (
    <div className="flex h-full flex-col">
      {/* -- Header -- */}
      <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-3 dark:border-zinc-700">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(templatesListPath)}
            className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {template.display_name || template.name}
          </h1>

          {/* Format badge */}
          <Badge
            label={formatLabel}
            colorClass={
              TEMPLATE_FORMAT_COLORS[template.format] ??
              'bg-zinc-100 text-zinc-600'
            }
          />

          <Popover open={versionPopoverOpen} onOpenChange={setVersionPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="cursor-pointer rounded-md bg-zinc-100 px-2 py-0.5 text-sm font-semibold tabular-nums text-zinc-600 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
              >
                v{template.version}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 max-h-[70vh] overflow-y-auto p-0" align="start">
              <div className="p-3">
                <TemplateVersionHistory
                  versionGroup={template.version_group}
                  currentVersion={template.version}
                  templateId={template.id as string}
                  templateFormat={template.format}
                  templateScope={template.scope}
                  uploadExpanded={uploadExpanded}
                  setUploadExpanded={setUploadExpanded}
                  onVersionCreated={(newId) => {
                    setVersionPopoverOpen(false);
                    navigate(buildTemplateDetailPath(newId));
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Action buttons in the header */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-1">
            {/* Branded preview — only for renderable global templates */}
            {template.scope === 'GLOBAL' &&
              !['EXTERNAL', 'HTML', 'SMS'].includes(template.format) && (
                <BrandedSamplePreview templateId={template.id as string} />
              )}

            <button
              type="button"
              onClick={() =>
                navigate(buildTemplateGeneratePath(String(template.id)))
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            >
              <PlayCircle className="h-3.5 w-3.5" />
              Generate
            </button>

            {template.file_url && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => downloadTemplate(template.id as string, template.display_name || template.name)}
                    className="rounded-md p-2 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Download file</TooltipContent>
              </Tooltip>
            )}

            {!['EXTERNAL', 'HTML', 'SMS'].includes(template.format) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => {
                      setVersionPopoverOpen(true);
                      setUploadExpanded(true);
                    }}
                    className="rounded-md p-2 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Upload new version</TooltipContent>
              </Tooltip>
            )}

            {template.format !== 'EXTERNAL' && (
              <button
                type="button"
                onClick={handleReanalyze}
                disabled={isReanalyzing}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isReanalyzing && 'animate-spin')} />
                {isReanalyzing ? 'Analyzing...' : 'Re-analyze'}
              </button>
            )}

            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      disabled={isDeleting}
                      className="rounded-md p-2 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Delete template</TooltipContent>
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Template</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete &quot;{template.name}&quot;?
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TooltipProvider>
      </div>

      {/* Signing config warning for retainer templates */}
      <AlertDialog open={showSigningConfigWarning} onOpenChange={setShowSigningConfigWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Missing Signing Configuration</AlertDialogTitle>
            <AlertDialogDescription>
                This retainer template requires a signing configuration before it
                can be saved. Without e-signatures configured, the retainer will
                not be sent for signing. Please configure signing to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setSigningConfigOpen(true);
              }}
            >
              Configure Signing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Global scope warning dialog */}
      <AlertDialog open={showGlobalScopeWarning} onOpenChange={setShowGlobalScopeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Global Template Warning</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;re about to save a globally scoped template. This could
              impact all firms. Are you sure you want to do this, or did you
              mean to scope it to a specific firm?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleSave();
              }}
            >
              Yes, Save Global Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* -- Resizable split pane -- */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left panel */}
        <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
          <div className="flex h-full flex-col overflow-y-auto">
            {/* Metadata form */}
            <div className="space-y-2 border-b border-zinc-200 px-3 py-2.5 dark:border-zinc-800">
              {/* Name + Category */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="template-name" className="mb-0.5 block text-[10px] font-medium uppercase text-zinc-500">
                    Name
                  </label>
                  <input
                    id="template-name"
                    type="text"
                    value={draft.name}
                    onChange={(e) => updateDraft('name', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="template-category" className="mb-0.5 block text-[10px] font-medium uppercase text-zinc-500">
                    Category
                  </label>
                  <select
                    id="template-category"
                    value={draft.category}
                    onChange={(e) => updateDraft('category', e.target.value)}
                    className={selectClass}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Scope + Default */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label htmlFor="template-is-default" className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                    <input
                      id="template-is-default"
                      type="checkbox"
                      checked={draft.is_default}
                      onChange={(e) =>
                        updateDraft('is_default', e.target.checked)
                      }
                      className="h-3 w-3 rounded border-zinc-300 text-primary focus:ring-primary dark:border-zinc-600"
                    />
                    Default
                  </label>
                  <label htmlFor="template-is-generic" className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                    <input
                      id="template-is-generic"
                      type="checkbox"
                      checked={draft.is_generic_template}
                      onChange={(e) =>
                        updateDraft('is_generic_template', e.target.checked)
                      }
                      className="h-3 w-3 rounded border-zinc-300 text-primary focus:ring-primary dark:border-zinc-600"
                    />
                    Generic (H/F swap)
                  </label>
                </div>
                <div className="flex items-center gap-0.5 rounded-md border border-zinc-200 p-0.5 dark:border-zinc-700">
                  {SCOPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateDraft('scope', opt.value)}
                      className={cn(
                        'rounded px-2 py-0.5 text-[11px] font-medium transition-colors',
                        draft.scope === opt.value
                          ? 'bg-primary text-white'
                          : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200',
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {draft.scope === 'FIRM' && (
                <FirmCombobox
                  value={draft.firm_id}
                  onChange={(firmId) => updateDraft('firm_id', firmId)}
                  className="w-full"
                />
              )}

              {/* Attorney */}
              {draft.firm_id ? (
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="template-attorney" className="mb-0.5 block text-[10px] font-medium uppercase text-zinc-500">
                    Attorney
                  </label>
                  <AttorneySelect
                    id="template-attorney"
                    firmId={draft.firm_id}
                    value={draft.attorney_id || null}
                    onChange={(attorneyId) => updateDraft('attorney_id', attorneyId ?? '')}
                    placeholder="Select attorney"
                  />
                </div>
              ) : template.attorney_name ? (
                <div>
                  <span className="mb-0.5 block text-[10px] font-medium uppercase text-zinc-500">
                    Attorney
                  </span>
                  <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                    {template.attorney_name}
                  </p>
                </div>
              ) : null}

              {/* Description */}
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="template-description" className="mb-0.5 block text-[10px] font-medium uppercase text-zinc-500">
                  Description
                </label>
                <textarea
                  id="template-description"
                  value={draft.description}
                  onChange={(e) => updateDraft('description', e.target.value)}
                  rows={1}
                  className={cn(inputClass, 'min-h-[32px] resize-y')}
                />
              </div>

              {/* Signing config (collapsible) */}
              {template.signing_config ? (
                <TemplateSigningPanel
                  templateId={template.id as string}
                  signingConfig={template.signing_config}
                  firmId={template.firm_id}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setSigningConfigOpen(true)}
                  className="flex w-full items-center justify-between rounded-lg border border-dashed border-zinc-300 px-3 py-2 text-xs font-medium text-zinc-500 transition-colors hover:border-primary hover:text-primary dark:border-zinc-700 dark:hover:border-primary"
                >
                  <span>No signing configuration</span>
                  <span className="text-primary">+ Configure</span>
                </button>
              )}
              <SigningConfigEditor
                open={signingConfigOpen}
                onClose={() => setSigningConfigOpen(false)}
                templateId={template.id as string}
                signingConfig={template.signing_config}
              />

              {/* Footer row: dates + save/discard */}
              <div className="flex items-center justify-between border-t border-zinc-100 pt-1.5 dark:border-zinc-800">
                <p className="text-[10px] font-medium text-zinc-500">
                  {formatShortDate(template.created_at)} · {formatShortDate(template.modified_at)}
                </p>
                {isDirty && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handleDiscard}
                      className="rounded px-2 py-0.5 text-[11px] font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handleSaveClick}
                      className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-[11px] font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Fields + Images */}
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-2">
              <TemplateFieldsPanel
                templateId={template.id as string}
                templateFormat={template.format}
              />

              {/* Images (DOCX/PDF only) */}
              {hasImageSupport && (
                <div className="mt-3 border-t border-zinc-200 pt-2 dark:border-zinc-800">
                  <Collapsible open={imagesOpen} onOpenChange={setImagesOpen}>
                    <CollapsibleTrigger className="flex w-full items-center justify-between py-1">
                      <div className="flex items-center gap-1.5">
                        <ImageIcon className="h-3 w-3 text-zinc-500" />
                        <span className="text-[11px] font-medium text-zinc-500">
                          Images
                        </span>
                        <span className="inline-flex items-center rounded bg-zinc-100 px-1.5 py-px text-[10px] font-semibold tabular-nums text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                          {imageCount}
                        </span>
                      </div>
                      <ChevronDownIcon
                        className={cn(
                          'h-3.5 w-3.5 text-zinc-400 transition-transform',
                          imagesOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pt-1">
                        <TemplateImagesPanel
                          templateId={template.id as string}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>

        {/* Resize handle */}
        <ResizableHandle withHandle />

        {/* Right panel — preview */}
        <ResizablePanel defaultSize={65} minSize={40}>
          <div className="h-full bg-zinc-50 dark:bg-zinc-900/50">
            {template.format === 'HTML' ? (
              <ResizablePanelGroup direction="vertical">
                <ResizablePanel defaultSize={55} minSize={25}>
                  <div className="h-full overflow-y-auto">
                    <HtmlTemplateEditor
                      initialBody={draft.body}
                      onBodyChange={(body) => updateDraft('body', body)}
                      readOnly={false}
                    />
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={45} minSize={20}>
                  <div className="h-full overflow-y-auto">
                    <HtmlPreview body={draft.body} />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            ) : template.format === 'SMS' ? (
              <ResizablePanelGroup direction="vertical">
                <ResizablePanel defaultSize={55} minSize={25}>
                  <div className="h-full overflow-y-auto">
                    <SmsTemplateEditor
                      initialBody={draft.body}
                      onBodyChange={(body) => updateDraft('body', body)}
                      readOnly={false}
                    />
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={45} minSize={20}>
                  <div className="h-full overflow-y-auto">
                    <SmsPreview body={draft.body} />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            ) : (
              <TemplatePreviewPanel template={template} />
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
